import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MagnifyingGlass, Sparkle, MapPin, ArrowRight, Buildings, ChatText, SlidersHorizontal, Database } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const PRESETS = [
    "Construction companies",
    "Architects",
    "Interior designers",
    "Solar companies",
    "Real estate developers",
    "Manufacturers",
    "UPVC manufacturers",
];

const PROMPT_EXAMPLES = [
    "Boutique D2C skincare brands in Southeast Asia with 11–50 employees that recently raised seed funding",
    "Mid-market SaaS companies in Europe selling to HR teams, likely needing performance marketing",
    "Premium real estate developers in Dubai launching luxury residential projects this year",
];

export default function Discover() {
    const [mode, setMode] = useState("structured"); // 'structured' | 'prompt'
    const [source] = useState("apify");
    const [enabledProviders, setEnabledProviders] = useState([]);
    const [industry, setIndustry] = useState("");
    const [location, setLocation] = useState("");
    const [keywords, setKeywords] = useState("");
    const [prompt, setPrompt] = useState("");
    const [count, setCount] = useState(8);
    const [minStars, setMinStars] = useState("");
    const [minReviews, setMinReviews] = useState("");
    const [radiusKm, setRadiusKm] = useState("");
    const [skipClosed, setSkipClosed] = useState(false);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    const apifyReady = enabledProviders.includes("apify");

    useEffect(() => {
        api.get("/integrations/enabled")
            .then((r) => setEnabledProviders(r.data.providers || []))
            .catch(() => {});
    }, []);

    const search = async (e) => {
        e?.preventDefault();
        if (!apifyReady) {
            toast.error("Connect Apify in Settings to discover leads");
            return;
        }
        if (mode === "structured" && !industry.trim()) {
            toast.error("Industry is required");
            return;
        }
        if (mode === "prompt" && !prompt.trim()) {
            toast.error("Please describe what you're looking for");
            return;
        }
        setLoading(true);
        setResults([]);
        try {
            const body = mode === "prompt"
                ? { prompt, count, source }
                : { industry, location, keywords, count, source };
            if (minStars !== "") body.min_stars = Number(minStars);
            if (minReviews !== "") body.min_reviews = Number(minReviews);
            if (radiusKm !== "") body.radius_km = Number(radiusKm);
            body.skip_closed = skipClosed;
            const { data } = await api.post("/leads/discover", body);
            const leads = data.leads || data;
            const dup = data.duplicates_count || 0;
            setResults(leads);
            if (dup > 0) {
                toast.info(`${dup} of ${data.requested} were duplicates. Re-run to fetch ${dup} more?`, {
                    action: { label: "Re-run", onClick: () => { setCount(dup); search(); } },
                });
            } else {
                toast.success(`Discovered ${leads.length} companies`);
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Discovery failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="discover-page">
            <div>
                <div className="overline text-muted-foreground">Lead Discovery</div>
                <h1 className="font-heading text-4xl sm:text-5xl font-black mt-2 leading-none">
                    Find your next 100 clients.
                </h1>
                <p className="text-muted-foreground mt-3 max-w-2xl">
                    Tell the AI who you want to reach — via structured filters or a free-form prompt — and
                    Scout AI will generate a list of qualified company leads in seconds.
                </p>
            </div>

            <form onSubmit={search} className="card-os p-6">
                {/* Source + Mode toggles */}
                <div className="flex items-center gap-4 flex-wrap mb-5">
                    <div className="flex items-center gap-2">
                        <Database size={14} className="text-muted-foreground" />
                        <span className="overline text-muted-foreground">Source:</span>
                        <span className="chip border-border bg-muted text-foreground" data-testid="discover-source-label">
                            Apify (Google Maps)
                        </span>
                        {!apifyReady && (
                            <span className="text-[11px] text-muted-foreground">
                                Connect Apify in <Link to="/settings" className="text-brand underline">Settings</Link> to discover leads
                            </span>
                        )}
                    </div>
                    <div className="inline-flex border border-border rounded-sm overflow-hidden">
                        <button
                            type="button"
                            data-testid="mode-structured"
                            onClick={() => setMode("structured")}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                                mode === "structured" ? "bg-foreground text-white" : "bg-white text-foreground hover:bg-muted"
                            }`}
                        >
                            <SlidersHorizontal size={14} /> Structured
                        </button>
                        <button
                            type="button"
                            data-testid="mode-prompt"
                            onClick={() => setMode("prompt")}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm border-l border-border transition-colors ${
                                mode === "prompt" ? "bg-foreground text-white" : "bg-white text-foreground hover:bg-muted"
                            }`}
                        >
                            <ChatText size={14} /> Prompt
                        </button>
                    </div>
                </div>

                {mode === "structured" ? (
                    <div className="grid md:grid-cols-12 gap-3">
                        <div className="md:col-span-5">
                            <label className="overline text-muted-foreground">Industry / Category *</label>
                            <input
                                data-testid="discover-industry"
                                value={industry} onChange={(e) => setIndustry(e.target.value)}
                                className="input-os mt-1" placeholder="e.g. Solar companies, Architects"
                            />
                        </div>
                        <div className="md:col-span-3">
                            <label className="overline text-muted-foreground">Location</label>
                            <input
                                data-testid="discover-location"
                                value={location} onChange={(e) => setLocation(e.target.value)}
                                className="input-os mt-1" placeholder="e.g. Mumbai, EU"
                            />
                        </div>
                        <div className="md:col-span-3">
                            <label className="overline text-muted-foreground">Keywords</label>
                            <input
                                data-testid="discover-keywords"
                                value={keywords} onChange={(e) => setKeywords(e.target.value)}
                                className="input-os mt-1" placeholder="commercial, premium"
                            />
                        </div>
                        <div className="md:col-span-1">
                            <label className="overline text-muted-foreground">Count</label>
                            <input
                                data-testid="discover-count"
                                type="number" min={1} max={15} value={count}
                                onChange={(e) => setCount(Number(e.target.value))}
                                className="input-os mt-1"
                            />
                        </div>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-12 gap-3">
                        <div className="md:col-span-11">
                            <label className="overline text-muted-foreground">Describe your ideal lead *</label>
                            <textarea
                                data-testid="discover-prompt"
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                rows={3}
                                className="input-os mt-1 resize-none leading-relaxed"
                                placeholder="e.g. Boutique D2C skincare brands in Southeast Asia with 11–50 employees that recently raised seed funding"
                            />
                        </div>
                        <div className="md:col-span-1">
                            <label className="overline text-muted-foreground">Count</label>
                            <input
                                data-testid="discover-prompt-count"
                                type="number" min={1} max={15} value={count}
                                onChange={(e) => setCount(Number(e.target.value))}
                                className="input-os mt-1"
                            />
                        </div>
                    </div>
                )}

                {/* Presets / examples */}
                <div className="flex flex-wrap gap-2 mt-4">
                    <span className="overline text-muted-foreground self-center">Try:</span>
                    {(mode === "structured" ? PRESETS : PROMPT_EXAMPLES).map((p, i) => (
                        <button
                            type="button"
                            key={i}
                            data-testid={`${mode}-preset-${i}`}
                            onClick={() => mode === "structured" ? setIndustry(p) : setPrompt(p)}
                            className="chip border-border text-muted-foreground hover:border-foreground hover:text-foreground max-w-full text-left"
                        >
                            <span className="truncate">{p}</span>
                        </button>
                    ))}
                </div>

                {/* Apify structured filters */}
                {source === "apify" && (
                    <div className="mt-4 border-t border-border pt-4" data-testid="apify-filters">
                        <div className="flex items-center gap-2 mb-3">
                            <MapPin size={14} className="text-brand" />
                            <span className="overline text-muted-foreground">Google Maps filters (Apify)</span>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                                <label className="overline text-muted-foreground">Min rating</label>
                                <select
                                    data-testid="apify-min-stars"
                                    value={minStars}
                                    onChange={(e) => setMinStars(e.target.value)}
                                    className="input-os mt-1"
                                >
                                    <option value="">Any</option>
                                    <option value="3">3.0+ ★</option>
                                    <option value="3.5">3.5+ ★</option>
                                    <option value="4">4.0+ ★</option>
                                    <option value="4.5">4.5+ ★</option>
                                </select>
                            </div>
                            <div>
                                <label className="overline text-muted-foreground">Min reviews</label>
                                <input
                                    data-testid="apify-min-reviews"
                                    type="number" min={0} value={minReviews}
                                    onChange={(e) => setMinReviews(e.target.value)}
                                    className="input-os mt-1" placeholder="e.g. 10"
                                />
                            </div>
                            <div>
                                <label className="overline text-muted-foreground">Search radius</label>
                                <select
                                    data-testid="apify-radius"
                                    value={radiusKm}
                                    onChange={(e) => setRadiusKm(e.target.value)}
                                    className="input-os mt-1"
                                >
                                    <option value="">Default</option>
                                    <option value="5">5 km</option>
                                    <option value="10">10 km</option>
                                    <option value="25">25 km</option>
                                    <option value="50">50 km</option>
                                </select>
                            </div>
                            <div className="flex items-end">
                                <label className="inline-flex items-center gap-2 text-sm cursor-pointer pb-2" data-testid="apify-skip-closed-label">
                                    <input
                                        data-testid="apify-skip-closed"
                                        type="checkbox"
                                        checked={skipClosed}
                                        onChange={(e) => setSkipClosed(e.target.checked)}
                                        className="h-4 w-4 accent-foreground"
                                    />
                                    Exclude permanently closed
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end mt-5">
                    <button
                        data-testid="discover-submit"
                        type="submit" disabled={loading}
                        className="btn-ai disabled:opacity-60"
                    >
                        {loading ? (
                            <>
                                <span className="h-2 w-2 bg-white rounded-full animate-pulse-soft" />
                                Discovering with AI…
                            </>
                        ) : (
                            <>
                                <Sparkle size={16} weight="fill" />
                                Discover with AI
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Results */}
            <div>
                {loading && (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="card-os p-5">
                                <div className="h-3 w-24 bg-muted rounded-sm animate-pulse-soft" />
                                <div className="h-5 w-40 bg-muted rounded-sm mt-3 animate-pulse-soft" />
                                <div className="h-3 w-full bg-muted rounded-sm mt-3 animate-pulse-soft" />
                                <div className="h-3 w-4/5 bg-muted rounded-sm mt-2 animate-pulse-soft" />
                            </div>
                        ))}
                    </div>
                )}
                {!loading && results.length > 0 && (
                    <>
                        <div className="flex items-center justify-between mb-3">
                            <div className="overline text-muted-foreground">{results.length} new leads</div>
                            <Link to="/pipeline" className="text-sm text-brand font-medium hover:underline" data-testid="goto-pipeline">
                                View in Pipeline →
                            </Link>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {results.map((l) => (
                                <Link
                                    key={l.id}
                                    to={`/leads/${l.id}`}
                                    className="card-os p-5 hover:shadow-md transition-shadow"
                                    data-testid={`result-card-${l.id}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="chip border-border bg-muted text-muted-foreground">
                                            <Buildings size={11} /> {l.industry}
                                        </span>
                                    </div>
                                    <h3 className="font-heading font-bold text-xl mt-3 leading-tight">{l.company_name}</h3>
                                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{l.description}</p>
                                    <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
                                        <span className="inline-flex items-center gap-1">
                                            <MapPin size={12} /> {l.location || "—"}
                                        </span>
                                        <span className="inline-flex items-center gap-1 text-brand font-medium">
                                            Research <ArrowRight size={12} />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </>
                )}
                {!loading && results.length === 0 && (
                    <div className="card-os p-12 text-center">
                        <MagnifyingGlass size={32} className="mx-auto text-muted-foreground" />
                        <h3 className="font-heading text-xl font-bold mt-3">Start your first search</h3>
                        <p className="text-muted-foreground text-sm mt-1">
                            Use structured filters or write a free-form prompt — the AI handles the rest.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
